import React, { useState, useMemo } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  Alert,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';

import {
  DashboardCard,
  FilterDrawer,
  FilterState,
  getAppliedFilterCount,
  DurationSelector,
  GranularityToggle,
  MetricBreakdownPanel,
} from '../../../../../shared/components';
import { buildFilterParams, useResolvedGranularity } from '../../shared/utils';
import { useClientScope } from '../../../../../shared/contexts';
import {
  usePersistedDateRange,
  usePersistedFilters,
  PROFILE_BREAKDOWN_DIMENSIONS,
  useComparisonMode,
  useComparisonGroups,
} from '../../../../../shared/hooks';
import type { ComparisonDimension } from '../../../../../shared/hooks';
import { AiSummaryPanel, useAiSummary } from '../../../insights';

import {
  useComparisonDemographics,
  useComparisonHrv,
  useComparisonRecovery,
  useComparisonStrain,
  useComparisonSleep,
} from '../hooks';

import { DemographicsSection } from '../components/DemographicsSection';
import { HrvGauge, HrvTrendChart } from '../components/HrvSection';
import { RecoveryGauge, RecoveryDistribution, RecoveryTrend } from '../components/RecoverySection';
import { StrainGauge, StrainZones, StrainTrend } from '../components/StrainSection';
import { SleepGauge, SleepStages, SleepTrend } from '../components/SleepSection';

import type { SleepTrendMetric, RecoveryTrendMetric, StrainTrendMetric } from '../api/types';

export const EmployeeProfileComparison: React.FC = () => {
  const { effectiveClientId, isCentralAdmin } = useClientScope();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [sleepMetric, setSleepMetric] = useState<SleepTrendMetric>('sleep_stages');
  const [recoveryMetric, setRecoveryMetric] = useState<RecoveryTrendMetric>('recovery_score');
  const [strainMetric, setStrainMetric] = useState<StrainTrendMetric>('day_strain');
  const [expandedBreakdown, setExpandedBreakdown] = useState<string | null>(null);
  const toggleBreakdown = (metric: string) => setExpandedBreakdown((prev) => (prev === metric ? null : metric));
  const { filters, setFilters } = usePersistedFilters();
  const activeFiltersCount = getAppliedFilterCount(filters);
  const { dateRange, setDateRange } = usePersistedDateRange();
  const { granularityMode, setGranularityMode, granularity } = useResolvedGranularity(dateRange);
  // The profile-comparison endpoints don't support hourly granularity.
  const resolvedGranularity = granularity === 'hourly' ? 'daily' : granularity;

  const filterParams = useMemo(
    () => buildFilterParams(filters, dateRange, effectiveClientId, null, null, { isCentralAdmin }),
    [filters, dateRange, effectiveClientId, isCentralAdmin],
  );

  // Comparison mode: when 2+ values of a filter dimension are selected, the
  // user can compare across them — one column per value. Distinct storage
  // key so this tab's compare state doesn't collide with the seven
  // analytics tabs' shared 'peak4.admin.comparison.v1' key.
  //
  // Unlike the seven analytics tabs, this tab has no "average" (blended
  // cohort) view — comparison is the only thing it renders. So we don't
  // expose the Average/Compare toggle at all: comparison activates
  // automatically as soon as a dimension becomes eligible (2+ values
  // selected), falling back to the hook's own priority order when the user
  // hasn't explicitly picked one via the "Compare by" select below.
  const comparison = useComparisonMode(filters, { storageKey: 'peak4.admin.profileComparison.v1' });
  const activeDimension = comparison.dimension ?? comparison.eligible[0] ?? null;
  const effectiveComparison = useMemo(
    () => ({ enabled: activeDimension !== null, dimension: activeDimension }),
    [activeDimension],
  );
  const { groups, isComparing, overflowCount } = useComparisonGroups(
    filters,
    dateRange,
    effectiveClientId,
    effectiveComparison,
    isCentralAdmin,
  );

  // Fetch data per section, fanned out one request per group.
  const demographics = useComparisonDemographics(groups, resolvedGranularity);
  const hrv = useComparisonHrv(groups, resolvedGranularity);
  const recovery = useComparisonRecovery(groups, resolvedGranularity);
  const strain = useComparisonStrain(groups, resolvedGranularity);
  const sleep = useComparisonSleep(groups, resolvedGranularity);

  const { buildPayload: buildAiPayload, disabled: aiDisabled } = useAiSummary({
    dashboard: 'Employee Profile Comparison',
    groups,
    isComparing,
    dimension: activeDimension,
    filterParams,
    metricSpecs: [
      { label: 'Recovery Score', keys: ['recovery_score'] },
      { label: 'Strain', keys: ['strain', 'day_strain'] },
      { label: 'HRV', keys: ['hrv', 'average_hrv'] },
      { label: 'Sleep Performance', keys: ['sleep_performance'] },
    ],
  });

  const removeGroup = (groupKey: string) => {
    const dimension = activeDimension;
    if (!dimension) return;
    setFilters({ ...filters, [dimension]: filters[dimension].filter((value) => value !== groupKey) });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Stack direction='row' spacing={2} alignItems='center' sx={{ flexWrap: 'wrap' }}>
          {comparison.eligible.length > 1 && activeDimension && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant='body2' color='text.secondary'>
                Compare by
              </Typography>
              <Select
                size='small'
                value={activeDimension}
                onChange={(event: SelectChangeEvent) =>
                  comparison.setDimension(event.target.value as ComparisonDimension)
                }
                sx={{ minWidth: 140 }}
              >
                {comparison.eligible.map((d) => (
                  <MenuItem key={d} value={d}>
                    {comparison.label(d)}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}
          <DurationSelector value={dateRange} onChange={setDateRange} />
          <GranularityToggle value={granularityMode} onChange={setGranularityMode} />
          <Button variant='outlined' startIcon={<FilterListIcon />} onClick={() => setIsFilterOpen(true)}>
            Filters{activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ''}
          </Button>
        </Stack>
      </Box>

      {overflowCount > 0 && (
        <Alert severity='info' sx={{ mb: 3 }}>
          Showing the first {groups.length} groups. {overflowCount} more {overflowCount === 1 ? 'is' : 'are'} hidden —
          narrow your selection to compare fewer at once.
        </Alert>
      )}

      {/* Empty state */}
      {!isComparing && (
        <Alert severity='info' sx={{ mb: 3 }}>
          Select 2 or more values of a single filter — region, depot, department, job role, age range, sex or shift
          pattern — to compare employee groups.
        </Alert>
      )}

      {/* Horizontally scrollable comparison columns */}
      {isComparing && (
        <Box sx={{ overflowX: 'auto', pb: 2 }}>
          {/* CSS Grid: columns = comparison groups, rows = sections. Each row aligns across all columns. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${groups.length}, minmax(350px, 1fr))`,
              gridAutoRows: 'auto',
              gap: 3,
              alignItems: 'stretch',
            }}
          >
            {/* Row: group headers */}
            {groups.map((group) => (
              <Box
                key={`header-${group.key}`}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}
              >
                <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: group.color, flexShrink: 0 }} />
                <Typography variant='h6' sx={{ fontWeight: 600, lineHeight: 1 }}>
                  {group.label}
                </Typography>
                <IconButton
                  size='small'
                  onClick={() => removeGroup(group.key)}
                  aria-label={`Remove ${group.label}`}
                  sx={{ border: 1, borderColor: 'divider', borderRadius: '50%', p: 0.5 }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))}

            {/* Row: Demographics */}
            {groups.map((group) => (
              <Box key={`demographics-${group.key}`} sx={{ display: 'flex', flexDirection: 'column' }}>
                <DemographicsSection
                  deptName={group.label}
                  demographics={demographics.byKey[group.key]?.demographics}
                  workingHourPatterns={demographics.byKey[group.key]?.working_hour_patterns}
                  ageDistribution={demographics.byKey[group.key]?.age_distribution}
                  isLoading={demographics.isLoading}
                />
              </Box>
            ))}

            {/* Row: HRV Gauge */}
            {groups.map((group) => (
              <Box key={`hrv-gauge-${group.key}`}>
                <HrvGauge
                  stats={hrv.byKey[group.key]?.stats}
                  isLoading={hrv.isLoading}
                  showBreakdown
                  breakdownOpen={expandedBreakdown === 'hrv'}
                  onToggleBreakdown={() => toggleBreakdown('hrv')}
                />
              </Box>
            ))}

            {/* Row: HRV Breakdown */}
            {expandedBreakdown === 'hrv' &&
              groups.map((group) => (
                <Box key={`hrv-breakdown-${group.key}`}>
                  <DashboardCard title='HRV Breakdown' expandable={false}>
                    <MetricBreakdownPanel
                      metricId='hrv'
                      metricLabel='HRV'
                      unit='ms'
                      filterParams={group.filterParams}
                      dimensions={PROFILE_BREAKDOWN_DIMENSIONS}
                    />
                  </DashboardCard>
                </Box>
              ))}

            {/* Row: HRV Trend */}
            {groups.map((group) => (
              <Box key={`hrv-trend-${group.key}`}>
                <HrvTrendChart
                  deptName={group.label}
                  stats={hrv.byKey[group.key]?.stats}
                  trends={hrv.byKey[group.key]?.trends}
                  isLoading={hrv.isLoading}
                  clientId={effectiveClientId ?? null}
                  weatherFilters={group.filterParams}
                />
              </Box>
            ))}

            {/* Row: Recovery Gauge */}
            {groups.map((group) => (
              <Box key={`recovery-gauge-${group.key}`}>
                <RecoveryGauge
                  isLoading={recovery.isLoading}
                  weatherFilters={group.filterParams}
                  showBreakdown
                  breakdownOpen={expandedBreakdown === 'recovery'}
                  onToggleBreakdown={() => toggleBreakdown('recovery')}
                />
              </Box>
            ))}

            {/* Row: Recovery Breakdown */}
            {expandedBreakdown === 'recovery' &&
              groups.map((group) => (
                <Box key={`recovery-breakdown-${group.key}`}>
                  <DashboardCard title='Recovery Breakdown' expandable={false}>
                    <MetricBreakdownPanel
                      metricId='recovery_score'
                      metricLabel='Recovery'
                      unit='%'
                      filterParams={group.filterParams}
                      dimensions={PROFILE_BREAKDOWN_DIMENSIONS}
                    />
                  </DashboardCard>
                </Box>
              ))}

            {/* Row: Recovery Distribution */}
            {groups.map((group) => (
              <Box key={`recovery-dist-${group.key}`}>
                <RecoveryDistribution
                  distribution={recovery.byKey[group.key]?.distribution}
                  isLoading={recovery.isLoading}
                />
              </Box>
            ))}

            {/* Toggle: Recovery trends metric */}
            <Box
              sx={{
                gridColumn: '1 / -1',
                position: 'sticky',
                left: 0,
                zIndex: 1,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <ToggleButtonGroup
                value={recoveryMetric}
                exclusive
                onChange={(_, val) => {
                  if (val) setRecoveryMetric(val);
                }}
                size='small'
              >
                <ToggleButton value='recovery_score'>Recovery Score</ToggleButton>
                <ToggleButton value='hrv'>Heart Rate Variability</ToggleButton>
                <ToggleButton value='resting_heart_rate'>Resting Heart Rate</ToggleButton>
                <ToggleButton value='sleep_performance'>Sleep Performance</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Row: Recovery trend chart */}
            {groups.map((group) => (
              <Box key={`recovery-trend-${group.key}`}>
                <RecoveryTrend
                  deptName={group.label}
                  clientId={effectiveClientId ?? null}
                  weatherFilters={group.filterParams}
                  selectedMetric={recoveryMetric}
                />
              </Box>
            ))}

            {/* Row: Strain Gauge */}
            {groups.map((group) => (
              <Box key={`strain-gauge-${group.key}`}>
                <StrainGauge
                  isLoading={strain.isLoading}
                  weatherFilters={group.filterParams}
                  showBreakdown
                  breakdownOpen={expandedBreakdown === 'strain'}
                  onToggleBreakdown={() => toggleBreakdown('strain')}
                />
              </Box>
            ))}

            {/* Row: Strain Breakdown */}
            {expandedBreakdown === 'strain' &&
              groups.map((group) => (
                <Box key={`strain-breakdown-${group.key}`}>
                  <DashboardCard title='Strain Breakdown' expandable={false}>
                    <MetricBreakdownPanel
                      metricId='strain'
                      metricLabel='Strain'
                      filterParams={group.filterParams}
                      dimensions={PROFILE_BREAKDOWN_DIMENSIONS}
                    />
                  </DashboardCard>
                </Box>
              ))}

            {/* Row: Strain Zones */}
            {groups.map((group) => (
              <Box key={`strain-zones-${group.key}`}>
                <StrainZones heartRateZones={strain.byKey[group.key]?.heart_rate_zones} isLoading={strain.isLoading} />
              </Box>
            ))}

            {/* Toggle: Strain trends metric */}
            <Box
              sx={{
                gridColumn: '1 / -1',
                position: 'sticky',
                left: 0,
                zIndex: 1,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <ToggleButtonGroup
                value={strainMetric}
                exclusive
                onChange={(_, val) => {
                  if (val) setStrainMetric(val);
                }}
                size='small'
              >
                <ToggleButton value='day_strain'>Day Strain</ToggleButton>
                <ToggleButton value='cardio_load'>Cardio Load</ToggleButton>
                <ToggleButton value='heart_rate'>Heart Rate</ToggleButton>
                <ToggleButton value='calories_burn'>Calories Burned</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Row: Strain trend chart */}
            {groups.map((group) => (
              <Box key={`strain-trend-${group.key}`}>
                <StrainTrend
                  deptName={group.label}
                  clientId={effectiveClientId ?? null}
                  weatherFilters={group.filterParams}
                  selectedMetric={strainMetric}
                />
              </Box>
            ))}

            {/* Row: Sleep Gauge */}
            {groups.map((group) => (
              <Box key={`sleep-gauge-${group.key}`}>
                <SleepGauge
                  isLoading={sleep.isLoading}
                  weatherFilters={group.filterParams}
                  showBreakdown
                  breakdownOpen={expandedBreakdown === 'sleep'}
                  onToggleBreakdown={() => toggleBreakdown('sleep')}
                />
              </Box>
            ))}

            {/* Row: Sleep Breakdown */}
            {expandedBreakdown === 'sleep' &&
              groups.map((group) => (
                <Box key={`sleep-breakdown-${group.key}`}>
                  <DashboardCard title='Sleep Breakdown' expandable={false}>
                    <MetricBreakdownPanel
                      metricId='sleep_performance'
                      metricLabel='Sleep'
                      unit='%'
                      filterParams={group.filterParams}
                      dimensions={PROFILE_BREAKDOWN_DIMENSIONS}
                    />
                  </DashboardCard>
                </Box>
              ))}

            {/* Row: Sleep Stages */}
            {groups.map((group) => (
              <Box key={`sleep-stages-${group.key}`}>
                <SleepStages sleepStages={sleep.byKey[group.key]?.sleep_stages} isLoading={sleep.isLoading} />
              </Box>
            ))}

            {/* Toggle: Sleep trends metric */}
            <Box
              sx={{
                gridColumn: '1 / -1',
                position: 'sticky',
                left: 0,
                zIndex: 1,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <ToggleButtonGroup
                value={sleepMetric}
                exclusive
                onChange={(_, val) => {
                  if (val) setSleepMetric(val);
                }}
                size='small'
              >
                <ToggleButton value='sleep_stages'>Stages</ToggleButton>
                <ToggleButton value='total_sleep'>Total Sleep</ToggleButton>
                <ToggleButton value='efficiency'>Efficiency</ToggleButton>
                <ToggleButton value='sleep_debt'>Sleep Debt</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Row: Sleep trend chart */}
            {groups.map((group) => (
              <Box key={`sleep-trend-${group.key}`}>
                <SleepTrend
                  deptName={group.label}
                  trends={sleep.byKey[group.key]?.trends}
                  clientId={effectiveClientId ?? null}
                  weatherFilters={group.filterParams}
                  selectedMetric={sleepMetric}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* AI Summary */}
      {isComparing && (
        <Box sx={{ mt: 4 }}>
          <DashboardCard title='AI Summary' expandable={false}>
            <AiSummaryPanel
              title={groups.length > 1 ? 'Compare these groups with AI' : 'Summarise this group with AI'}
              buildPayload={buildAiPayload}
              disabled={aiDisabled || demographics.isLoading || hrv.isLoading}
            />
          </DashboardCard>
        </Box>
      )}

      {/* Filter Drawer */}
      <FilterDrawer
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={(newFilters: FilterState) => {
          setFilters(newFilters);
          setIsFilterOpen(false);
        }}
        initialFilters={filters}
      />
    </Box>
  );
};
